import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AttachmentIcon from "@material-ui/icons/Attachment";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";

import {
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Tooltip,
  Fade,
  Avatar,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles";

import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: theme.palette.type === 'light' ? '#f5f5f5' : theme.palette.background.default,
    padding: theme.spacing(4),
    overflow: "hidden", // Prevents scroll on the container itself
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  titleContainer: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontWeight: 800,
    color: theme.palette.primary.main,
    fontSize: "2.125rem", // h4 size
    lineHeight: 1.235,
    marginBottom: theme.spacing(0.5),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem", // body2
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  searchContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.background.paper,
    borderRadius: "50px", // Rounded search
    padding: "6px 20px",
    width: "300px",
    boxShadow: "0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)", // Small shadow for depth
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease",
    "&:focus-within": {
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      borderColor: theme.palette.primary.main,
    },
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    fontSize: "0.95rem",
  },
  searchIcon: {
    color: theme.palette.text.secondary,
  },
  addButton: {
    borderRadius: "50px",
    padding: "8px 24px",
    fontWeight: "bold",
    textTransform: "none",
    boxShadow: theme.shadows[3],
    "&:hover": {
      boxShadow: theme.shadows[6],
    },
  },
  gridContainer: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden", // Fix horizontal scroll
    ...theme.scrollbarStyles,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2), // Adjust padding for scrollbar
    paddingLeft: theme.spacing(1),
  },
  card: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: "16px",
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none", // Flat by default
    transition: "all 0.3s ease",
    backgroundColor: theme.palette.background.paper,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
      borderColor: "transparent",
    },
  },
  cardContent: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: theme.spacing(2),
  },
  shortcodeContainer: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    padding: "4px 12px",
    borderRadius: "12px",
    transition: "background-color 0.2s",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
    }
  },
  shortcode: {
    fontWeight: 800,
    fontSize: "1rem",
    color: theme.palette.primary.main,
    wordBreak: "break-all",
  },
  messageText: {
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
    lineHeight: 1.6,
    display: "-webkit-box",
    "-webkit-line-clamp": 5,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
    whiteSpace: "pre-wrap",
  },
  cardActions: {
    padding: theme.spacing(2, 3),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  badges: {
    display: "flex",
    gap: theme.spacing(1),
  },
  avatarIcon: {
    width: 32,
    height: 32,
    borderRadius: 8, // Rounded square
    backgroundColor: "transparent",
    color: theme.palette.text.secondary,
    "&.media": {
      color: theme.palette.info.main,
      backgroundColor: alpha(theme.palette.info.main, 0.1),
    },
    "&.active": {
      color: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, 0.1),
    }

  },
  iconButton: {
    borderRadius: 8,
    padding: 8,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main
    }
  }
}));

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];
    const newState = [...state];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = newState.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          newState[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...newState, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      const newState = [...state];
      newState[quickemessageIndex] = quickemessage;
      return newState;
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;

    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      const newState = [...state];
      newState.splice(quickemessageIndex, 1);
      return newState;
    }
    return state;
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Quickemessages = () => {
  const classes = useStyles();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;

    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickemessage`, onQuickMessageEvent);

    return () => {
      socket.off(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    };
  }, [socket, user.companyId]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <div className={classes.mainContainer}>
      <ConfirmationModal
        title={deletingQuickemessage && `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />

      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography className={classes.title}>
            {i18n.t("quickMessages.title")}
          </Typography>
          <Typography className={classes.subtitle}>
            Gerencie suas respostas rápidas e otimize seu atendimento
          </Typography>
        </div>

        <div className={classes.headerActions}>
          <div className={classes.searchContainer}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              placeholder={i18n.t("quickMessages.searchPlaceholder")}
              classes={{
                root: classes.searchInput,
              }}
              value={searchParam}
              onChange={handleSearch}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenQuickMessageDialog}
            className={classes.addButton}
            startIcon={<AddIcon />}
          >
            {i18n.t("quickMessages.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.gridContainer} onScroll={handleScroll}>
        <Grid container spacing={4}>
          {quickemessages.map((quickemessage) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={quickemessage.id}>
              <Fade in={true} timeout={500}>
                <Card className={classes.card}>
                  <CardContent className={classes.cardContent}>
                    <div className={classes.cardHeader}>
                      <div
                        className={classes.shortcodeContainer}
                        onClick={() => handleEditQuickemessage(quickemessage)}
                      >
                        <Typography className={classes.shortcode}>
                          /{quickemessage.shortcode}
                        </Typography>
                      </div>
                      <div className={classes.badges}>
                        {quickemessage.mediaPath && (
                          <Tooltip title={i18n.t("quickMessages.table.mediaName")}>
                            <Avatar className={`${classes.avatarIcon} media`}>
                              <AttachmentIcon fontSize="small" />
                            </Avatar>
                          </Tooltip>
                        )}
                        {quickemessage.geral && (
                          <Tooltip title={i18n.t("quickMessages.table.status")}>
                            <Avatar className={`${classes.avatarIcon} active`}>
                              <CheckCircleIcon fontSize="small" />
                            </Avatar>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    <Typography
                      variant="body2"
                      component="p"
                      className={classes.messageText}
                    >
                      {quickemessage.message}
                    </Typography>
                  </CardContent>
                  <CardActions className={classes.cardActions}>
                    <Tooltip title={i18n.t("quickMessages.buttons.edit")}>
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => handleEditQuickemessage(quickemessage)}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={i18n.t("quickMessages.buttons.delete")}>
                      <IconButton
                        className={classes.iconButton}
                        onClick={() => {
                          setConfirmModalOpen(true);
                          setDeletingQuickemessage(quickemessage);
                        }}
                        size="small"
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </CardActions>
                </Card>
              </Fade>
            </Grid>
          ))}
          {loading && (
            <Grid item xs={12}>
              <Typography variant="body2" align="center" color="textSecondary" style={{ padding: 20 }}>
                {i18n.t("quickMessages.loading")}
              </Typography>
            </Grid>
          )}
        </Grid>
      </div>
    </div>
  );
};

export default Quickemessages;